// Shared DTOs between apps/mail (Worker API) and apps/mail-web (PWA client).
// These mirror the D1 + MailboxDO SQLite schemas but are transport-shaped
// (camelCase, ISO date strings) rather than raw row shapes.

export type PermissionLevel = "owner" | "full" | "read_only" | "send_as";

export type SystemFolderSlug =
  | "inbox"
  | "archive"
  | "sent"
  | "drafts"
  | "trash"
  | "spam";

export interface User {
  id: string;
  email: string;
  /** The LOGIN identity (Phase 8.1) — chosen by the user themselves during account setup, NULL until they complete that flow. `email` stays in the system for account-recovery (forgot-password) only once a username exists. */
  username: string | null;
  displayName: string;
  createdAt: string;
}

export interface Mailbox {
  id: string;
  address: string;
  displayName: string;
  createdAt: string;
  /**
   * Phase 9: per-mailbox signature, plain text only (this app has no
   * rich-text editor anywhere — see compose-sheet.tsx's own docstring).
   * Null/empty means nothing is prefilled on a new compose. Genuinely
   * per-mailbox, not per-user: "only one user will be able to set the
   * signature for each mailbox... for each mailbox they own individually"
   * (client's own framing) — editable only by that mailbox's owner
   * (requireMailbox("admin"), see lib/acl.ts) but visible/applied for
   * every user who composes as this mailbox, at any permission level.
   */
  signature: string | null;
  /**
   * Out-of-office auto-reply (Phase 9). Same ownership model as signature.
   * `oooMessage` is what actually gets sent back to a sender when enabled —
   * see lib/ooo.ts for the anti-loop design (auto-submitted/bulk-mail
   * detection, spam-folder exclusion, per-sender rate limiting) that gates
   * whether an inbound message actually triggers a reply.
   */
  oooEnabled: boolean;
  oooMessage: string | null;
}

/** Enforced both client-side (instant feedback in the admin signature/OOO editors) and server-side (routes/mailboxes.ts) — the client-side check is only a courtesy, never trusted alone. */
export const MAX_SIGNATURE_LENGTH = 2000;
export const MAX_OOO_MESSAGE_LENGTH = 2000;

export interface MailboxPermission {
  mailboxId: string;
  userId: string;
  permissionLevel: PermissionLevel;
}

/**
 * A mailbox as seen from "the mailboxes I can access" (GET /api/me, GET
 * /api/mailboxes) — a Mailbox plus the permission level the current user
 * holds on it. Kept separate from the base Mailbox DTO (used e.g. as the
 * bare `mailbox` field in ProvisionMailboxResponse, where "current user's
 * level" isn't a meaningful concept yet) rather than adding an optional
 * field to it everywhere.
 */
export interface MailboxMembership extends Mailbox {
  permissionLevel: PermissionLevel;
}

export interface Folder {
  id: string;
  mailboxId: string;
  slug: SystemFolderSlug | string;
  name: string;
  isSystem: boolean;
  sortOrder: number;
}

/**
 * User-organizable folders (Phase 6). One folder per thread — moving a
 * thread to a folder removes it from wherever it was, never adds a second
 * location (see docs/architecture/mail-architecture.md's locked folder-
 * model decision). System folders (isSystem === true) can never be renamed
 * or deleted — enforced server-side in MailboxDO#renameFolder/deleteFolder,
 * not just hidden client-side.
 */
export const MAX_FOLDER_NAME_LENGTH = 40;

export interface CreateFolderRequest {
  name: string;
}

export interface CreateFolderResponse {
  folder: Folder;
}

export interface RenameFolderRequest {
  name: string;
}

export interface RenameFolderResponse {
  folder: Folder;
}

/** Deleting a folder that still has threads in it moves them to Archive rather than orphaning them or blocking the delete — see MailboxDO#deleteFolder. */
export interface DeleteFolderResponse {
  ok: boolean;
  movedThreadCount: number;
}

export interface Attachment {
  id: string;
  messageId: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  r2Key: string;
  contentId: string | null;
  isInline: boolean;
}

export type SendState =
  | "draft"
  | "pending"
  | "sending"
  | "sent"
  | "failed"
  | "cancelled";

export interface MessageAuth {
  spfPass: boolean | null;
  dkimPass: boolean | null;
  dmarcPass: boolean | null;
}

export interface EmailParticipant {
  name: string | null;
  email: string;
}

export interface Message {
  id: string;
  threadId: string;
  mailboxId: string;
  messageIdHeader: string | null;
  inReplyTo: string | null;
  referencesHeader: string | null;
  from: EmailParticipant;
  to: EmailParticipant[];
  cc: EmailParticipant[];
  bcc: EmailParticipant[];
  subject: string;
  snippet: string;
  textBody: string | null;
  htmlBody: string | null;
  direction: "inbound" | "outbound";
  sendState: SendState;
  scheduledSendAt: string | null;
  auth: MessageAuth;
  attachments: Attachment[];
  createdAt: string;
}

export interface Thread {
  id: string;
  mailboxId: string;
  folderId: string;
  subject: string;
  participants: EmailParticipant[];
  messageCount: number;
  unread: boolean;
  lastMessageAt: string;
  snippet: string;
}

export interface ThreadWithMessages extends Thread {
  messages: Message[];
}

// ---- API request/response shapes ----

export interface ListThreadsResponse {
  threads: Thread[];
  nextCursor: string | null;
}

export interface GetThreadResponse {
  thread: ThreadWithMessages;
}

export interface MoveThreadRequest {
  folderId: string;
}

export interface SetThreadUnreadRequest {
  unread: boolean;
}

export interface SetThreadUnreadResponse {
  thread: Thread;
}

// ---- Search (D1 FTS5-backed, see routes/search.ts) ----

export interface SearchResultItem {
  messageId: string;
  threadId: string;
  folderId: string;
  subject: string;
  /** Plain-text excerpt from the message body with matched search terms
   *  delimited by the U+0001 (SOH) control character on both sides. The
   *  client splits on that delimiter and highlights the odd-indexed
   *  segments -- this is plain text, never raw HTML, so no sanitization is
   *  needed to render it. */
  snippet: string;
  senderEmail: string;
  lastMessageAt: string;
}

/**
 * `similar` (Phase 7, semantic search) is the SAME item shape as `results`
 * deliberately — Vectorize/Workers AI never leak past the server boundary.
 * There is no score, no "similarity" field, nothing for a client to build
 * technical-sounding UI out of even by accident. The mail-web search
 * results view renders `similar` under a plain-language heading (something
 * like "You might also want to check") as a second, clearly-separated
 * section below the keyword `results` — never mixed into the same list,
 * since a non-technical user should be able to tell "this matched your
 * words" apart from "this seemed related" even though both render with the
 * same SearchResultRow component. Always already deduped against `results`
 * server-side (see lib/semantic-index.ts#findSimilarMessages's
 * `excludeMessageIds`) and ACL-scoped the same way `results` is.
 */
export interface SearchResponse {
  query: string;
  results: SearchResultItem[];
  similar: SearchResultItem[];
}

/**
 * Undo Send grace window: how long a queued outbound message waits before
 * actually being handed to the send_email binding, during which it can be
 * cancelled. Single source of truth shared by MailboxDO (arms its alarm to
 * this offset) and the compose UI (renders the "Sending in Ns... Undo"
 * countdown against it) — see docs/architecture/mail-architecture.md,
 * "Undo Send".
 */
export const UNDO_SEND_WINDOW_MS = 10_000;

export type SendMode = "new" | "reply" | "forward";

/**
 * A file uploaded via POST /mailboxes/:id/attachments/upload (see
 * routes/attachments.ts) but not yet attached to any message — the upload
 * puts the blob in R2 and hands the client back this reference, which it
 * then includes on the actual /messages/send call. Kept separate from
 * `Attachment` (which always has an `id`/`messageId` — it's a persisted
 * row) since this exists before any message row does.
 */
export interface UploadedAttachmentRef {
  r2Key: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
}

export interface UploadAttachmentResponse {
  attachment: UploadedAttachmentRef;
}

/**
 * Client-side guard for new attachment uploads — a friendly ceiling well
 * under Cloudflare Workers' request-body limits and the base64-encoding
 * overhead the send path applies (see lib/mail-sender.ts), not a hard
 * platform limit. Chosen to comfortably clear typical mechanic-shop
 * attachments (photos, PDF quotes/invoices) while keeping a non-technical
 * user from stalling out on a huge file — chunked upload is out of scope
 * for this pass. Enforced client-side for instant feedback and re-checked
 * server-side (routes/attachments.ts) since the client-side guard is only
 * a courtesy, not a security boundary.
 */
export const MAX_ATTACHMENT_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB per file
/** Combined cap across every newly-attached file on one outbound message — roughly matches common inbox provider limits (Gmail/Outlook cap around 25 MB per message). */
export const MAX_TOTAL_ATTACHMENTS_BYTES = 20 * 1024 * 1024; // 20 MB per message

export interface SendMessageRequest {
  mode: SendMode;
  /** Required for reply/forward — the thread the new message is appended to. Omit for "new" (a fresh thread is created). */
  threadId?: string;
  /** Required for reply/forward — the message being replied to/forwarded; supplies In-Reply-To/References and (client-side) the quoted content. */
  parentMessageId?: string;
  to: EmailParticipant[];
  cc?: EmailParticipant[];
  bcc?: EmailParticipant[];
  subject: string;
  html?: string;
  text?: string;
  /** Forward: original attachment ids (from parentMessageId) to carry over onto the new outbound message. */
  attachmentIds?: string[];
  /** Freshly-uploaded files (see UploadedAttachmentRef) to attach to this new outbound message — independent of, and combinable with, attachmentIds. */
  newAttachments?: UploadedAttachmentRef[];
}

export interface SendMessageResponse {
  outboxId: string;
  messageId: string;
  threadId: string;
  scheduledSendAt: string;
}

export interface UndoSendRequest {
  outboxId: string;
}

export interface UndoSendResponse {
  ok: boolean;
  status: "cancelled" | "too_late" | "not_found";
}

export interface RetrySendRequest {
  outboxId: string;
}

// ---- Staff mailbox delegation (Phase 5) ----
//
// Owner-only mailbox provisioning + ACL management, and a read-only
// cross-mailbox "All Inboxes" view for a user with access to more than one
// mailbox. See docs/architecture/mail-architecture.md, "Phase 2 product —
// Staff delegation" and the Phase 5 addendum for the design rationale
// (notably: why the D1 `folders` mirror table's ids were never usable for
// this, and why `thread_index` gained a `folder_slug` column instead).

/** One row in a mailbox's access list (GET/POST/DELETE .../permissions), with the user's identity resolved for display. */
export interface PermissionEntry {
  userId: string;
  email: string;
  displayName: string;
  permissionLevel: PermissionLevel;
  createdAt: string;
}

export interface ListPermissionsResponse {
  permissions: PermissionEntry[];
}

/**
 * Grants (or updates) one user's access to a mailbox. If no D1 `users` row
 * exists for `email` yet, one is created on the fly — this is the whole
 * "invite" flow for this MVP (see architecture doc: there was no prior
 * signup/invite concept at all, every session before this phase was a
 * single hardcoded seed user). `displayName` is only used the first time a
 * user is created for this email; ignored on a repeat grant to an existing
 * user.
 */
export interface GrantPermissionRequest {
  email: string;
  displayName?: string;
  permissionLevel: PermissionLevel;
}

export interface RevokePermissionResponse {
  ok: boolean;
}

/** Local-part only — the UI fixes the `@onemileauto.com` domain suffix so a non-technical user can't mistype or provision an address the mail system will never receive. */
export interface ProvisionMailboxRequest {
  localPart: string;
  displayName: string;
  /** Optional one-step "add a new hire": grant this email access in the same request. */
  grantEmail?: string;
  grantDisplayName?: string;
  /** Defaults to "full" server-side if grantEmail is given but this is omitted. */
  grantPermissionLevel?: PermissionLevel;
}

/** Outcome of attempting to create the matching Cloudflare Email Routing rule as part of provisioning — see lib/email-routing.ts. Automating this needs a zone/email_routing-scoped API token that isn't available in every environment (same class of gap as the Access-application-creation limitation documented for Phase 0/1/4), so this always reports honestly rather than assuming success. */
export interface EmailRoutingProvisionResult {
  /** Whether a CLOUDFLARE_API_TOKEN was configured to even attempt the API call. */
  attempted: boolean;
  created: boolean;
  /** Plain-English detail — either confirmation, or the exact manual step needed. */
  detail: string;
}

export interface ProvisionMailboxResponse {
  mailbox: Mailbox;
  folders: Folder[];
  /** Every permission row granted as part of this call (the provisioning owner, plus grantEmail if given). */
  granted: PermissionEntry[];
  routing: EmailRoutingProvisionResult;
  /**
   * Whether this address is currently in the deployed Worker's
   * `send_email` binding allow-list (see wrangler.jsonc's
   * ALLOWED_SENDER_ADDRESSES var, kept in sync by hand with the binding
   * config it mirrors). Bindings are static per-deploy config — a Worker
   * cannot add itself to its own allow-list at runtime — so `false` here
   * means the mailbox can receive mail immediately but cannot send until a
   * developer adds the address and redeploys.
   */
  sendEnabled: boolean;
}

/**
 * PATCH /api/mailboxes/:id/settings — signature + out-of-office auto-reply
 * (Phase 9), owner-only (requireMailbox("admin")). Partial update: an
 * omitted field is left unchanged; `null` explicitly clears a text field.
 * Server-side (routes/mailboxes.ts) refuses to set oooEnabled: true unless
 * a non-blank oooMessage is present (either already stored or given in the
 * same request) — an "on" switch with nothing to actually send would
 * silently do nothing, which is worse than rejecting it up front.
 */
export interface UpdateMailboxSettingsRequest {
  signature?: string | null;
  oooEnabled?: boolean;
  oooMessage?: string | null;
}

export interface UpdateMailboxSettingsResponse {
  mailbox: Mailbox;
}

/**
 * GET /api/admin/users — owner-only, system-wide (not scoped to mailboxes
 * the calling owner personally owns): this is a deliberately single-tenant
 * app (client decision, see architecture doc), so "all users" really does
 * mean everyone in this deployment. One row per user, with every mailbox
 * they have any level of access to and at what level — the "everything one
 * account can see" view the per-mailbox ACL panels (Phase 5) don't provide
 * on their own.
 */
export interface AdminUserAccess {
  mailboxId: string;
  mailboxAddress: string;
  mailboxDisplayName: string;
  permissionLevel: PermissionLevel;
}

export interface AdminUserSummary extends User {
  access: AdminUserAccess[];
}

export interface ListAllUsersResponse {
  users: AdminUserSummary[];
}

/**
 * Owner-only embeddings backfill (Phase 7, semantic search) — POST
 * /api/admin/backfill-embeddings. Idempotent (already-embedded messages are
 * skipped, not re-billed) and bounded per call (see
 * lib/semantic-index.ts#runEmbeddingBackfill) so this is safe to invoke
 * repeatedly; `hasMore: true` means re-invoke to continue.
 */
export interface BackfillEmbeddingsRequest {
  /** Total message cap for this call, across every mailbox — defaults server-side to a small, cost-conscious number. */
  limit?: number;
}

export interface BackfillEmbeddingsResponse {
  processed: number;
  skipped: number;
  hasMore: boolean;
  mailboxesTouched: string[];
}

/** One row in the cross-mailbox "All Inboxes" view (GET /api/threads) — a thread_index row joined with its owning mailbox's identity. Read-only/navigational: clicking through opens the thread in its real mailbox via GET /api/mailboxes/:id/threads/:threadId, the same detail view used everywhere else. */
export interface AggregatedThreadItem {
  threadId: string;
  mailboxId: string;
  mailboxAddress: string;
  mailboxDisplayName: string;
  subject: string;
  participants: EmailParticipant[];
  messageCount: number;
  unread: boolean;
  snippet: string;
  lastMessageAt: string;
}

export interface ListAggregatedThreadsResponse {
  threads: AggregatedThreadItem[];
  nextCursor: string | null;
}

// ---- Password authentication (Phase 8) ----
//
// Replaces Cloudflare Access entirely — see
// docs/architecture/mail-architecture.md's Phase 8 addendum for the full
// rationale (a client product decision: Access's hosted-login branding is
// account-wide, not per-application, and Access has no username/password or
// SMS login option). Session is a signed JWT in an HttpOnly cookie (see
// apps/mail/src/lib/session.ts) — these DTOs are intentionally minimal and
// carry no role/permission data: this pass is authn-only, and
// `mailbox_permissions` (unchanged since Phase 5) remains the sole authz
// source of truth.

/** Enforced both client-side (instant feedback on the reset-password page) and server-side (routes/auth.ts — the client-side check is only a courtesy, never trusted alone). */
export const MIN_PASSWORD_LENGTH = 8;

/**
 * Phase 8.1: login switched from email to a plain, client-chosen username
 * ("has some swag to it", per the client) — email stays in the system for
 * account-recovery (forgot-password) only. Bounds and the allowed character
 * set are enforced both client-side (instant feedback on the reset-password
 * page's account-setup step) and server-side (routes/auth.ts, plus a
 * belt-and-suspenders D1 CHECK constraint — see migration 0005_username.sql)
 * — the client-side check is only a courtesy, never trusted alone.
 */
export const MIN_USERNAME_LENGTH = 3;
export const MAX_USERNAME_LENGTH = 20;
const USERNAME_PATTERN = /^[a-z0-9][a-z0-9_-]*$/;

/** Trim + lowercase — the same normalization this app already applies to email addresses (see apps/mail's lib/d1.ts) — applied before validating or comparing a username anywhere. */
export function normalizeUsername(raw: string): string {
  return raw.trim().toLowerCase();
}

/** True iff `username` (expected already run through normalizeUsername) is a well-formed login username: lowercase letters/digits plus `_`/`-`, starting with a letter or digit, 3-20 characters. */
export function isValidUsernameFormat(username: string): boolean {
  return (
    username.length >= MIN_USERNAME_LENGTH &&
    username.length <= MAX_USERNAME_LENGTH &&
    USERNAME_PATTERN.test(username)
  );
}

/** Plain-language description of the username format rule, shared so server 400s and client inline hints never drift apart in wording. */
export const USERNAME_FORMAT_HINT = `${MIN_USERNAME_LENGTH}-${MAX_USERNAME_LENGTH} characters: lowercase letters, numbers, "_" or "-", starting with a letter or number.`;

export interface LoginRequest {
  username: string;
  password: string;
}

export interface LoginResponse {
  user: User;
}

export interface ForgotPasswordRequest {
  email: string;
}

/** Always this same shape/message regardless of whether `email` matched a real account, and regardless of whether the request was rate-limited — see routes/auth.ts for the anti-enumeration reasoning (matching the pattern Cloudflare's own OTP page uses). */
export interface ForgotPasswordResponse {
  ok: true;
}

/**
 * Consumes a single-use setup or reset token (the two are functionally
 * identical from this endpoint's point of view) and sets a new password.
 * `username` is required IFF the token's underlying user hasn't chosen one
 * yet (see GET /api/auth/token-info, which tells the client whether to even
 * show that field) — ignored otherwise, since this endpoint has no
 * change-username facility for a user who already has one.
 */
export interface ResetPasswordRequest {
  token: string;
  password: string;
  username?: string;
}

export interface ResetPasswordResponse {
  ok: true;
}

/** GET /api/auth/token-info?token=... — a non-consuming peek so the reset-password page knows, before the user submits anything, whether to show the "choose a username" step. A 400 (invalid/expired/used token) is handled the same generic way the real reset-password submission already is; this type only covers the success shape. */
export interface TokenInfoResponse {
  needsUsername: boolean;
}

/** GET /api/auth/check-username?username=... — live availability feedback while a user is choosing one during account setup. Always 200 (even for a malformed username) so the page can show one consistent inline message; `detail` is only present when `available` is false. */
export interface CheckUsernameResponse {
  available: boolean;
  detail?: string;
}

// ---- Recipient autocomplete (derived from correspondence history) ----
//
// NOT a manually-managed address book — every row is derived automatically
// from real message history (an inbound sender, or an outbound to/cc/bcc
// recipient once the send actually succeeds) and kept current as new mail is
// sent/received. See apps/mail's db/do/schema.sql `contacts` table docstring
// and MailboxDO#upsertContacts/#listContacts/#backfillContacts for the full
// design. Strictly per-mailbox — GET /api/mailboxes/:id/contacts is gated at
// the same "read" ACL level as every other read route, so a user with access
// to more than one mailbox never sees one mailbox's correspondence history
// bleed into another's suggestions.

export interface ContactSuggestion {
  email: string;
  /** Most-recently-seen display name for this address; null if every message involving it lacked one. */
  name: string | null;
  interactionCount: number;
  lastInteractedAt: string;
}

/**
 * GET /api/mailboxes/:id/contacts?q=... — an empty/missing `q` returns this
 * mailbox's top recent/frequent contacts (see lib/contacts.ts#rankContacts),
 * which is what lets the compose UI show useful suggestions the instant a
 * recipient field is focused, before any typing.
 */
export interface ListContactsResponse {
  contacts: ContactSuggestion[];
}

/**
 * Owner-only, POST /api/admin/backfill-contacts — rebuilds every mailbox's
 * `contacts` table from scratch by replaying its own message history.
 * Unlike Phase 7's embeddings backfill, this needs no bounding/pagination
 * across calls: it's pure DO-local SQL/JS with no external per-message API
 * cost, so one call fully (and idempotently — it's a full recompute, not an
 * incremental catch-up) rebuilds every mailbox in the system.
 */
export interface BackfillContactsResponse {
  mailboxes: Array<{ mailboxId: string; messagesProcessed: number; contactCount: number }>;
}

// ---- WebSocket events (MailboxDO -> client) ----

export type MailboxEvent =
  | { type: "message:new"; threadId: string; message: Message }
  // Emitted the instant a send is queued (still inside the Undo Send grace
  // window) so every connected client — including the tab that submitted it
  // — can render the "Sending in Ns... Undo" affordance from one source of
  // truth (message.scheduledSendAt). Re-emitted on retry.
  | { type: "message:sending"; threadId: string; outboxId: string; message: Message }
  | { type: "message:sent"; threadId: string; outboxId: string; messageId: string }
  | { type: "message:cancelled"; threadId: string; outboxId: string; messageId: string }
  | { type: "message:failed"; threadId: string; outboxId: string; messageId: string; error: string }
  | { type: "thread:moved"; threadId: string; folderId: string }
  | { type: "thread:updated"; thread: Thread }
  // Phase 6 (user-organizable folders): folder CRUD broadcasts so every
  // connected client's folder nav / move-to-folder picker stays live
  // without a manual refresh, same principle as every other mutating event
  // here. folder:deleted's threads were already moved to Archive
  // server-side by the time this fires (see MailboxDO#deleteFolder) — the
  // client just needs to know to drop the folder from its list and refetch
  // threads, not replay individual thread:moved events for each one.
  | { type: "folder:created"; folder: Folder }
  | { type: "folder:updated"; folder: Folder }
  | { type: "folder:deleted"; folderId: string; movedToFolderId: string };
